import React, { useState, useEffect } from 'react';

const VEHICLES = [
    { id: 'scooter', name: 'Petrol Scooter', mileage: 45, evEfficiency: 60, isDiesel: false, premium: 40000 },
    { id: 'small_car', name: 'Petrol Car (Small/Hatchback)', mileage: 18, evEfficiency: 7.5, isDiesel: false, premium: 150000 },
    { id: 'mid_car', name: 'Petrol Car (Mid-size/SUV)', mileage: 14, evEfficiency: 6.5, isDiesel: false, premium: 200000 },
    { id: 'diesel_car', name: 'Diesel Car', mileage: 20, evEfficiency: 6.5, isDiesel: true, premium: 200000 },
];

export default function EVSavingsCalculator() {
    const [selectedVehicle, setSelectedVehicle] = useState(VEHICLES[1].id);
    const [kmPerMonth, setKmPerMonth] = useState(1000);
    const [fuelPrice, setFuelPrice] = useState(105);
    const [electricityRate, setElectricityRate] = useState(8);

    const [results, setResults] = useState({
        monthlyFuelCost: 0,
        monthlyEVCost: 0,
        monthlySavings: 0,
        annualSavings: 0,
        paybackMonths: 0,
        co2SavedYearly: 0
    });

    useEffect(() => {
        const vehicle = VEHICLES.find(v => v.id === selectedVehicle);
        
        // Update default fuel price if diesel vs petrol (just a UX touch, normally diesel is slightly cheaper)
        // We'll keep it simple and just use the user input.

        const monthlyFuelCost = (kmPerMonth / vehicle.mileage) * fuelPrice;
        const monthlyEVCost = (kmPerMonth / vehicle.evEfficiency) * electricityRate;
        
        const monthlySavings = monthlyFuelCost - monthlyEVCost;
        const annualSavings = monthlySavings * 12;
        
        const paybackMonths = monthlySavings > 0 ? vehicle.premium / monthlySavings : 0;
        
        // Using the 0.82 kg offset per kWh rule from specs
        const monthlyUnits = kmPerMonth / vehicle.evEfficiency;
        const co2SavedYearly = (monthlyUnits * 0.82) * 12;

        setResults({
            monthlyFuelCost: Math.round(monthlyFuelCost),
            monthlyEVCost: Math.round(monthlyEVCost),
            monthlySavings: Math.round(monthlySavings),
            annualSavings: Math.round(annualSavings),
            paybackMonths: Math.round(paybackMonths),
            co2SavedYearly: Math.round(co2SavedYearly)
        });
    }, [selectedVehicle, kmPerMonth, fuelPrice, electricityRate]);

    const activeVehicle = VEHICLES.find(v => v.id === selectedVehicle);

    return (
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
            <div className="bg-gradient-to-r from-green-500 to-emerald-600 px-6 py-8 text-white text-center">
                <h2 className="text-3xl font-bold mb-2">EV Savings Calculator</h2>
                <p className="text-emerald-100">See how much you can save by switching from {activeVehicle.isDiesel ? 'Diesel' : 'Petrol'} to Electric.</p>
            </div>
            
            <div className="p-6 sm:p-8 grid grid-cols-1 lg:grid-cols-2 gap-10">
                {/* Inputs */}
                <div className="space-y-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Current Vehicle Type</label>
                        <select 
                            className="w-full border-gray-300 rounded-lg shadow-sm focus:border-emerald-500 focus:ring-emerald-500 p-3 border"
                            value={selectedVehicle}
                            onChange={(e) => setSelectedVehicle(e.target.value)}
                        >
                            {VEHICLES.map(v => (
                                <option key={v.id} value={v.id}>{v.name}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <div className="flex justify-between mb-2">
                            <label className="text-sm font-medium text-gray-700">Kilometres Driven Per Month</label>
                            <span className="text-sm font-bold text-emerald-600">{kmPerMonth} km</span>
                        </div>
                        <input 
                            type="range" 
                            min="100" 
                            max="5000" 
                            step="100"
                            value={kmPerMonth}
                            onChange={(e) => setKmPerMonth(Number(e.target.value))}
                            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-emerald-600"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">{activeVehicle.isDiesel ? 'Diesel' : 'Petrol'} Price (₹/L)</label>
                            <div className="relative rounded-md shadow-sm">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <span className="text-gray-500 sm:text-sm">₹</span>
                                </div>
                                <input 
                                    type="number" 
                                    className="w-full pl-8 border-gray-300 rounded-lg shadow-sm focus:border-emerald-500 focus:ring-emerald-500 p-3 border"
                                    value={fuelPrice}
                                    onChange={(e) => setFuelPrice(Number(e.target.value))}
                                    min="1"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Electricity Rate (₹/kWh)</label>
                            <div className="relative rounded-md shadow-sm">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <span className="text-gray-500 sm:text-sm">₹</span>
                                </div>
                                <input 
                                    type="number" 
                                    className="w-full pl-8 border-gray-300 rounded-lg shadow-sm focus:border-emerald-500 focus:ring-emerald-500 p-3 border"
                                    value={electricityRate}
                                    onChange={(e) => setElectricityRate(Number(e.target.value))}
                                    min="1"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Outputs */}
                <div className="bg-gray-50 rounded-xl p-6 sm:p-8 flex flex-col justify-center border border-gray-100 relative">
                    <div className="text-center mb-8">
                        <p className="text-sm text-gray-500 font-medium uppercase tracking-wide">Estimated Annual Savings</p>
                        <p className="text-5xl font-extrabold text-emerald-600 mt-2">₹{results.annualSavings.toLocaleString('en-IN')}</p>
                        <p className="text-sm font-semibold text-gray-700 mt-2">That's ₹{results.monthlySavings.toLocaleString('en-IN')} saved every month!</p>
                    </div>

                    <div className="space-y-4 mb-8">
                        <div className="flex justify-between items-center py-3 border-b border-gray-200">
                            <span className="text-gray-600">Monthly {activeVehicle.isDiesel ? 'Diesel' : 'Petrol'} Cost</span>
                            <span className="font-semibold text-red-500">₹{results.monthlyFuelCost.toLocaleString('en-IN')}</span>
                        </div>
                        <div className="flex justify-between items-center py-3 border-b border-gray-200">
                            <span className="text-gray-600">Monthly EV Charging Cost</span>
                            <span className="font-semibold text-emerald-600">₹{results.monthlyEVCost.toLocaleString('en-IN')}</span>
                        </div>
                        <div className="flex justify-between items-center py-3 border-b border-gray-200">
                            <span className="text-gray-600">Payback on EV Premium</span>
                            <span className="font-semibold text-gray-900">{results.paybackMonths} months</span>
                        </div>
                        <div className="flex justify-between items-center py-3">
                            <span className="text-gray-600">Yearly CO₂ Saved</span>
                            <span className="font-semibold text-emerald-600">{results.co2SavedYearly.toLocaleString('en-IN')} kg 🌍</span>
                        </div>
                    </div>

                    <a href="https://play.google.com/store/apps/details?id=com.niloy.peerchargerev" target="_blank" rel="noopener noreferrer" className="w-full block text-center bg-gray-900 text-white font-bold py-4 px-6 rounded-xl hover:bg-black transition-colors shadow-md hover:shadow-lg">
                        Already own an EV? Find affordable charging.
                    </a>
                </div>
            </div>
        </div>
    );
}
